import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Routes } from '@angular/router';

// Environment

// Modules
import { NgxSpinnerModule } from 'ngx-spinner';

// Components
import { AppComponent } from './app.component';

// Ui Kit - Components

// Firebase

// AngularFire AuthGuard Services
import {
  AngularFireAuthGuard,
  customClaims,
  redirectUnauthorizedTo,
} from '@angular/fire/compat/auth-guard';

//FontAwesome
import { ENVIRONMENT } from '@environment';
import {
  FaIconLibrary,
  FontAwesomeModule,
} from '@fortawesome/angular-fontawesome';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import {
  faArrowRight,
  faArrowRightToBracket,
  faArrowRotateLeft,
  faCalendarDays,
  faCheck,
  faCopy,
  faEdit,
  faEllipsis,
  faGrip,
  faInfoCircle,
  faList,
  faPenToSquare,
  faRightFromBracket,
  faSearch,
  faSortDown,
  faSortUp,
  faArrowLeft as fasArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import {
  faFolder,
  faFolderOpen,
  faSparkles,
  faSpinner,
  faTable,
  faTriangleExclamation,
} from '@fortawesome/pro-light-svg-icons';
import {
  faChartColumn,
  faCirclePlus,
  faCogs,
  faGripDotsVertical,
  faListCheck,
  faPlay,
  faTimesCircle,
  faArrowLeft as farArrowLeft,
  faCircleCheck,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faEye,
  faEyeSlash,
  faSlidersHSquare,
  faTrash,
  faUserPen,
} from '@fortawesome/pro-solid-svg-icons';
import { CoreModule } from '@noovle/core';
import { NavBarComponent } from '@noovle/ui-nav-bar';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { map, pipe } from 'rxjs';
import { environment } from '../environments/environment';

const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo(['auth']);

const hasCustomToken = () =>
  pipe(
    customClaims,
    map((claims) => (claims.hasOwnProperty('tenant') ? true : ['auth']))
  );

const redirectLoggedInToHome = () =>
  pipe(
    customClaims,
    map((claims) => (claims.hasOwnProperty('tenant') ? ['pages'] : true))
  );

const routes: Routes = [
  { path: '', redirectTo: '/pages', pathMatch: 'full' },
  {
    path: 'auth',
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectLoggedInToHome },
    loadChildren: () => import('@noovle/auth').then((m) => m.AuthModule),
  },
  {
    path: 'pages',
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: hasCustomToken },
    loadChildren: () =>
      import('./page-manager/page-manager.module').then(
        (m) => m.PageManagerModule
      ),
  },
  {
    path: 'users',
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin },
    loadChildren: () =>
      import('./users/users.module').then((m) => m.UsersModule),
  },
  {
    path: 'chart-types',
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin },
    loadChildren: () =>
      import('./chart-types/chart-types.module').then(
        (m) => m.ChartTypesModule
      ),
  },
  {
    path: 'dashboards',
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin },
    loadChildren: () =>
      import('./dashboards/dashboards.module').then((m) => m.DashboardsModule),
  },
  {
    path: 'auditing',
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin },
    loadChildren: () =>
      import('./auditing/auditing.module').then((m) => m.AuditingModule),
  },
  {
    path: 'custom-metrics',
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin },
    loadChildren: () =>
      import('./custom-metric/custom-metric.module').then(
        (m) => m.CustomMetricModule
      ),
  },
];

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    RouterModule.forRoot(routes),
    FontAwesomeModule,
    HttpClientModule,
    // UI Kit
    NavBarComponent,
    BrowserAnimationsModule,
    NgxSpinnerModule,
    NgMultiSelectDropDownModule.forRoot(),
    CoreModule.forRoot(environment.firebase),
  ],
  providers: [{ provide: ENVIRONMENT, useValue: environment }],
})
export class AppModule {
  constructor(library: FaIconLibrary) {
    library.addIcons(
      faCopy,
      faPenToSquare,
      faSearch,
      faTrash,
      faChartColumn,
      fasArrowLeft,
      farArrowLeft,
      faArrowRight,
      faSortDown,
      faSortUp,
      faCirclePlus,
      faGoogle,
      faEye,
      faArrowRotateLeft,
      faEdit,
      faRightFromBracket,
      faArrowRightToBracket,
      faList,
      faGrip,
      faEllipsis,
      faUserPen,
      faCalendarDays,
      faCheck,
      faFolder,
      faFolderOpen,
      faEyeSlash,
      faTable,
      faCogs,
      faSparkles,
      faSpinner,
      faInfoCircle,
      faGripDotsVertical,
      faTimesCircle,
      faPlay,
      faListCheck,
      faSlidersHSquare,
      faTriangleExclamation,
      faCircleCheck
    );
  }
}
